import * as Sentry from "@sentry/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import axios from "axios"
import { format, min, parse, subDays } from "date-fns"
import { Badge } from "design-system/atoms/Badge"
import { Card } from "design-system/molecules/Card"
import { ChartConfig } from "design-system/molecules/Chart"
import DateDisplay from "design-system/molecules/DateDisplay"
import { FilterMenu, FilterMenuField } from "design-system/molecules/FilterMenu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { MetricCard } from "design-system/organisms/insights/MetricCard"
import { StackedActivityChart } from "design-system/organisms/insights/StackedActivityChart"
import { setAuthToken } from "modules/auth/lib/setAuthToken"
import { CompanySelector } from "modules/backoffice/components/CompanySelector"
import { BackofficeConversationExperimentsMultiSelect } from "modules/backoffice/components/experiments/BackofficeConversationExperimentsMultiSelect"
import { useGetBackofficeCapabilityExecutionsAnalyticsQuery } from "modules/backoffice/hooks/capabilityExecutions/useGetBackofficeCapabilityExecutionsAnalyticsQuery"
import { BACKOFFICE_RECENT_TIME_FRAME_OPTIONS } from "modules/backoffice/lib/backofficeRecentTimeFrameOptions"
import { getBackofficeConversationExperimentNamesQuery } from "modules/backoffice/lib/experiments/getBackofficeConversationExperimentNamesQuery"
import { getBackofficeActivityOpacity } from "modules/backoffice/lib/getBackofficeActivityOpacity"
import { useCapabilitiesDictionary } from "modules/capabilities/hooks/useCapabilitiesDictionary"
import { TimeRangeSelector } from "modules/shared/components/TimeRange/TimeRangeSelector"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { Pagination } from "modules/shared/components/Pagination"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Route } from "routes/_base/_auth/backoffice/capabilityExecutions/route"
import {
  BackofficeCapabilityExecutionActionEnum,
  BackofficeCapabilityExecutionProviderEnum,
  BackofficeCapabilityExecutionsAnalyticsResponseDataSchema,
  BackofficeCapabilityExecutionsSearchSchema,
  BackofficeImpersonateOrganizationResponseDataSchema,
  CapabilityExecutionStatus,
  CapabilityIdentifier,
  CapabilityReasonValues,
  DataResponse,
  getTimeRange,
  resolveCapabilityReasonLabel,
} from "schema"
import { z } from "zod"
import { CapabilityDistributionTreemap } from "./CapabilityDistributionTreemap"
import { SelectedCapabilityMetrics } from "./SelectedCapabilityMetrics"

const ACTIVITY_RANGE_DAYS = 30
const COLUMN_COUNT = 8
const CAPABILITY_LABEL_MAX_LENGTH = 30
const REASON_MAX_LENGTH = 50

const STATUS_COLORS = {
  [CapabilityExecutionStatus.Success]: "var(--success)",
  [CapabilityExecutionStatus.Unsuccess]: "var(--testing-1)",
  [CapabilityExecutionStatus.Cached]: "var(--muted-foreground)",
  [CapabilityExecutionStatus.Error]: "var(--destructive)",
  [CapabilityExecutionStatus.Skipped]: "var(--warning)",
} as const

const STATUS_SERIES = {
  [CapabilityExecutionStatus.Success]: "top1",
  [CapabilityExecutionStatus.Unsuccess]: "top2",
  [CapabilityExecutionStatus.Cached]: "top3",
  [CapabilityExecutionStatus.Error]: "top4",
  [CapabilityExecutionStatus.Skipped]: "top5",
} as const

const STATUS_VARIANTS = {
  [CapabilityExecutionStatus.Success]: "success",
  [CapabilityExecutionStatus.Unsuccess]: "unsuccess",
  [CapabilityExecutionStatus.Cached]: "cached",
  [CapabilityExecutionStatus.Error]: "negative",
  [CapabilityExecutionStatus.Skipped]: "warning",
} as const

type Search = z.infer<typeof BackofficeCapabilityExecutionsSearchSchema>
type AnalyticsData = z.infer<typeof BackofficeCapabilityExecutionsAnalyticsResponseDataSchema>
type Row = AnalyticsData["rows"][number]

function humanizeReason(reason: string) {
  return reason
    .split("_")
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ")
}

export const BackofficeCapabilityExecutions = () => {
  const { t } = useTranslation()
  const labels = useFilterBarLabels()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { buildLocation } = useRouter()
  const queryClient = useQueryClient()
  const capabilitiesDictionary = useCapabilitiesDictionary()
  const { data: experimentNames } = useQuery(
    getBackofficeConversationExperimentNamesQuery({ query: {} }),
  )

  const range = getTimeRange(search.timeFrame)
  const fromDate = range.from
  const toDate = min([range.to, new Date()])
  const to = format(toDate, "dd/MM/yyyy")
  const from = format(fromDate, "dd/MM/yyyy")
  const activityFrom = format(subDays(toDate, ACTIVITY_RANGE_DAYS - 1), "dd/MM/yyyy")
  const page = search.page ?? 1
  const total = search.total ?? 100
  const selectedCapabilities =
    search.capabilityIdentifier === "all"
      ? Object.values(CapabilityIdentifier)
      : search.capabilityIdentifier.split(",")
  const selectedCapabilityIdentifier =
    selectedCapabilities.length === 1 ? selectedCapabilities[0] : undefined
  const isUpdateOrder = selectedCapabilityIdentifier === CapabilityIdentifier.UpdateOrder

  const query = {
    companyId: search.companyId,
    experiment: search.experiment,
    from,
    activityFrom,
    to,
    page,
    total,
    status: search.status,
    reason: search.reason,
    provider: search.provider,
    capabilityIdentifier: search.capabilityIdentifier,
    action: search.action,
  } satisfies Search

  const { data, isPending } = useGetBackofficeCapabilityExecutionsAnalyticsQuery(query)
  const totalPages = data?.rowCount ? Math.ceil(data.rowCount / total) : undefined

  const chartData = useMemo(() => {
    const byDate = new Map<
      string,
      {
        date: string
        top1: number
        top2: number
        top3: number
        top4: number
        top5: number
        opacity: number
      }
    >()

    const chartToDate = parse(to, "dd/MM/yyyy", new Date())
    for (let index = ACTIVITY_RANGE_DAYS - 1; index >= 0; index -= 1) {
      const date = format(subDays(chartToDate, index), "yyyy-MM-dd")
      byDate.set(date, {
        date,
        top1: 0,
        top2: 0,
        top3: 0,
        top4: 0,
        top5: 0,
        opacity: getBackofficeActivityOpacity(date, search.timeFrame),
      })
    }

    for (const point of data?.timeSeries ?? []) {
      const current = byDate.get(point.date)
      if (!current) continue
      current[STATUS_SERIES[point.status]] += point.count
    }

    return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date))
  }, [data?.timeSeries, search.timeFrame, to])

  const chartConfig = useMemo(
    () =>
      ({
        top1: {
          label: t(`backoffice.capabilityExecutions.status.${CapabilityExecutionStatus.Success}`),
          color: STATUS_COLORS[CapabilityExecutionStatus.Success],
        },
        top2: {
          label: t(`backoffice.capabilityExecutions.status.${CapabilityExecutionStatus.Unsuccess}`),
          color: STATUS_COLORS[CapabilityExecutionStatus.Unsuccess],
        },
        top3: {
          label: t(`backoffice.capabilityExecutions.status.${CapabilityExecutionStatus.Cached}`),
          color: STATUS_COLORS[CapabilityExecutionStatus.Cached],
        },
        top4: {
          label: t(`backoffice.capabilityExecutions.status.${CapabilityExecutionStatus.Error}`),
          color: STATUS_COLORS[CapabilityExecutionStatus.Error],
        },
        top5: {
          label: t(`backoffice.capabilityExecutions.status.${CapabilityExecutionStatus.Skipped}`),
          color: STATUS_COLORS[CapabilityExecutionStatus.Skipped],
        },
      }) satisfies ChartConfig,
    [t],
  )

  const capabilityOptions = useMemo(
    () =>
      Object.values(CapabilityIdentifier)
        .map((identifier) => ({
          value: identifier,
          label: capabilitiesDictionary[identifier]?.name ?? identifier,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [capabilitiesDictionary],
  )

  const translatedReasonLabel = useCallback(
    (reason: string, capabilityIdentifier?: string) => {
      switch (reason) {
        case "missing_requirements":
          return t("backoffice.capabilityExecutions.reasons.missing_requirements")
        case "consecutive_skip_limit_reached":
          return t("backoffice.capabilityExecutions.reasons.consecutive_skip_limit_reached")
        case "consecutive_unsuccess_limit_reached":
          return t("backoffice.capabilityExecutions.reasons.consecutive_unsuccess_limit_reached")
        case "testing_environment":
          return t("backoffice.capabilityExecutions.reasons.testing_environment")
        case "unexpected_error":
          return t("backoffice.capabilityExecutions.reasons.unexpected_error")
        case "missing_fields":
          return t("backoffice.capabilityExecutions.reasons.missing_fields")
        case "verification_failed":
        case "Order verification failed":
          return t("backoffice.capabilityExecutions.reasons.verification_failed")
        case "verification_not_unique":
        case "Order verification was not unique":
          return t("backoffice.capabilityExecutions.reasons.verification_not_unique")
        case "order_email_missing":
          return t("backoffice.capabilityExecutions.reasons.order_email_missing")
        case "order_email_mismatch":
          return t("backoffice.capabilityExecutions.reasons.order_email_mismatch")
        case "order_contact_mismatch":
          return t("backoffice.capabilityExecutions.reasons.order_contact_mismatch")
        case "order_id_mismatch":
          return t("backoffice.capabilityExecutions.reasons.order_id_mismatch")
        case "confirmation_required":
          return t("backoffice.capabilityExecutions.reasons.confirmation_required")
        case "reason_required":
          return t("backoffice.capabilityExecutions.reasons.reason_required")
        case "already_cancelled":
          return t("backoffice.capabilityExecutions.reasons.already_cancelled")
        case "not_cancellable":
          return t("backoffice.capabilityExecutions.reasons.not_cancellable")
        case "window_passed":
          return t("backoffice.capabilityExecutions.reasons.window_passed")
        case "not_eligible":
          return t("backoffice.capabilityExecutions.reasons.not_eligible")
        case "shipping_address_missing":
          return t("backoffice.capabilityExecutions.reasons.shipping_address_missing")
        case "shipping_address_invalid":
          return t("backoffice.capabilityExecutions.reasons.shipping_address_invalid")
        case "shipping_country_change_not_supported":
          return t("backoffice.capabilityExecutions.reasons.shipping_country_change_not_supported")
        case "pickup_point_not_supported":
          return t("backoffice.capabilityExecutions.reasons.pickup_point_not_supported")
        case "recipient_name_missing":
          return t("backoffice.capabilityExecutions.reasons.recipient_name_missing")
        case "update_failed":
          return t("backoffice.capabilityExecutions.reasons.update_failed")
        case "priority_update_failed":
          return t("backoffice.capabilityExecutions.reasons.priority_update_failed")
        case "on_hold_failed":
          return t("backoffice.capabilityExecutions.reasons.on_hold_failed")
        case "already_shipped":
          return t("backoffice.capabilityExecutions.reasons.already_shipped")
        case "order_not_synced":
          return t("backoffice.capabilityExecutions.reasons.order_not_synced")
        case "order_not_found":
          return t("backoffice.capabilityExecutions.reasons.order_not_found")
        case "unsupported_action":
          return t("backoffice.capabilityExecutions.reasons.unsupported_action")
        case "order_update_multiple_products":
          return t("backoffice.capabilityExecutions.reasons.order_update_multiple_products")
        case "line_item_discounted":
          return t("backoffice.capabilityExecutions.reasons.line_item_discounted")
        case "product_update_multiple_products":
          return t("backoffice.capabilityExecutions.reasons.product_update_multiple_products")
        case "product_line_discounted":
          return t("backoffice.capabilityExecutions.reasons.product_line_discounted")
        case "product_variant_missing":
          return t("backoffice.capabilityExecutions.reasons.product_variant_missing")
        case "product_variant_ambiguous":
          return t("backoffice.capabilityExecutions.reasons.product_variant_ambiguous")
        case "product_variant_out_of_stock":
          return t("backoffice.capabilityExecutions.reasons.product_variant_out_of_stock")
        case "product_variant_price_mismatch":
          return t("backoffice.capabilityExecutions.reasons.product_variant_price_mismatch")
        case "line_item_id_missing":
          return t("backoffice.capabilityExecutions.reasons.line_item_id_missing")
        case "product_update_failed":
          return t("backoffice.capabilityExecutions.reasons.product_update_failed")
        case "shopify_product_update_failed":
          return t("backoffice.capabilityExecutions.reasons.shopify_product_update_failed")
        case "line_item_update_failed":
          return t("backoffice.capabilityExecutions.reasons.line_item_update_failed")
        case "shopify_line_item_update_failed":
          return t("backoffice.capabilityExecutions.reasons.shopify_line_item_update_failed")
        case "refund_not_found":
          return t("backoffice.capabilityExecutions.reasons.refund_not_found")
        case "refund_email_mismatch":
          return t("backoffice.capabilityExecutions.reasons.refund_email_mismatch")
        case "invalid_data":
          return t("backoffice.capabilityExecutions.reasons.invalid_data")
        case "missing_search_query":
          return t("backoffice.capabilityExecutions.reasons.missing_search_query")
        case "missing_required_fields":
          return t("backoffice.capabilityExecutions.reasons.missing_required_fields")
        case "contact_requirement_not_met":
          return t("backoffice.capabilityExecutions.reasons.contact_requirement_not_met")
        case "ambiguous_reference":
          return t("backoffice.capabilityExecutions.reasons.ambiguous_reference")
        case "no_grounded_reference":
          return t("backoffice.capabilityExecutions.reasons.no_grounded_reference")
        case "variant_ambiguous":
          return t("backoffice.capabilityExecutions.reasons.variant_ambiguous")
        case "variant_no_match":
          return t("backoffice.capabilityExecutions.reasons.variant_no_match")
        default:
          return (
            resolveCapabilityReasonLabel(
              capabilityIdentifier ?? selectedCapabilityIdentifier ?? "",
              reason,
            ) ?? humanizeReason(reason)
          )
      }
    },
    [selectedCapabilityIdentifier, t],
  )

  const reasonOptions = useMemo(() => {
    return CapabilityReasonValues.map((reason) => ({
      value: reason,
      label: translatedReasonLabel(reason),
    })).sort((a, b) => a.label.localeCompare(b.label))
  }, [translatedReasonLabel])

  const statusOptions = useMemo(() => {
    return [
      {
        value: "all",
        label: t("backoffice.capabilityExecutions.filters.allStatuses"),
      },
      ...Object.values(CapabilityExecutionStatus).map((status) => ({
        value: status,
        label: t(`backoffice.capabilityExecutions.status.${status}`),
      })),
    ]
  }, [t])

  const providerOptions = BackofficeCapabilityExecutionProviderEnum.map((provider) => ({
    value: provider,
    label: provider,
  }))

  const actionLabel = useCallback(
    (action: Row["action"]) => {
      switch (action) {
        case "shipping_address":
          return t("backoffice.capabilityExecutions.actions.shipping_address")
        case "delivery_priority":
          return t("backoffice.capabilityExecutions.actions.delivery_priority")
        case "on_hold":
          return t("backoffice.capabilityExecutions.actions.on_hold")
        case "line_item":
          return t("backoffice.capabilityExecutions.actions.line_item")
        case null:
          return "—"
      }
    },
    [t],
  )

  function updateSearch(next: Partial<typeof search>) {
    navigate({ search: { ...search, ...next } })
  }

  function onChangeFilter(
    key: "experiment" | "status" | "provider" | "action" | "reason",
    value: string | undefined,
  ) {
    updateSearch({ [key]: value === "all" ? undefined : value, page: 1 })
  }

  const fields: FilterMenuField[] = [
    {
      key: "experiment",
      label: t("backoffice.capabilityExecutions.filters.experiment"),
      value: search.experiment,
      onChange: (value) => onChangeFilter("experiment", value),
      options: Array.from(
        new Set([...(experimentNames ?? []), ...(search.experiment ? [search.experiment] : [])]),
        (name) => ({ value: name, label: name }),
      ),
    },
    {
      key: "provider",
      label: t("backoffice.capabilityExecutions.filters.provider"),
      value: search.provider,
      onChange: (value) => onChangeFilter("provider", value),
      options: providerOptions,
    },
    {
      key: "action",
      condition: isUpdateOrder,
      label: t("backoffice.capabilityExecutions.filters.action"),
      value: search.action,
      onChange: (value) => onChangeFilter("action", value),
      options: BackofficeCapabilityExecutionActionEnum.map((action) => ({
        value: action,
        label: actionLabel(action),
      })),
    },
    {
      key: "reason",
      label: t("backoffice.capabilityExecutions.filters.reason"),
      value: search.reason === "all" ? undefined : search.reason,
      onChange: (value) => onChangeFilter("reason", value),
      options: reasonOptions,
    },
  ]

  function capabilityLabel(identifier: string, fallback?: string | null) {
    return (
      fallback ?? capabilitiesDictionary[identifier as CapabilityIdentifier]?.name ?? identifier
    )
  }

  function reasonLabel(
    row: Pick<Row, "capabilityIdentifier" | "reason" | "blockedByCapabilityIdentifier">,
  ) {
    if (!row.reason) return "—"
    return translatedReasonLabel(
      row.reason,
      row.blockedByCapabilityIdentifier ?? row.capabilityIdentifier,
    )
  }

  function truncateReason(reason: string) {
    return reason.length > REASON_MAX_LENGTH ? `${reason.slice(0, REASON_MAX_LENGTH)}…` : reason
  }

  function truncateCapabilityLabel(label: string) {
    return label.length > CAPABILITY_LABEL_MAX_LENGTH
      ? `${label.slice(0, CAPABILITY_LABEL_MAX_LENGTH)}…`
      : label
  }

  async function openConversation(row: Row) {
    const link = buildLocation({
      to: "/conversations/list/$customerId",
      params: { customerId: row.customerId },
      search: { tab: "conversations", conversationId: Number(row.conversationId) },
    }).href

    try {
      type ImpersonateResponse = DataResponse<
        z.infer<typeof BackofficeImpersonateOrganizationResponseDataSchema>
      >

      const response = await axios.get<ImpersonateResponse>(
        `/backoffice/organizations/impersonate/${row.organizationId}`,
      )

      setAuthToken({ queryClient, token: response.data.data.token })
      window.open([window.location.origin, link].join(""), "_blank")
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          organizationId: row.organizationId,
          customerId: row.customerId,
          conversationId: row.conversationId,
          companyName: row.companyName,
        },
      })
      console.error("Failed to impersonate organization from capability executions", { error })
    }
  }

  function renderRows() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={Array.from({ length: COLUMN_COUNT }, () => 1)} />
    }

    if (data.rows.length === 0) {
      return (
        <TableEmptySlate
          colSpan={COLUMN_COUNT}
          message={t("backoffice.capabilityExecutions.table.empty")}
        />
      )
    }

    return data.rows.map((row) => (
      <TableRow key={row.id} className="cursor-pointer" onClick={() => openConversation(row)}>
        <TableCell className="pr-8">
          <div className="flex items-center gap-2">
            {row.companyLogo && <img src={row.companyLogo} alt="" style={{ height: 24 }} />}
            <span>{row.companyName}</span>
          </div>
        </TableCell>
        <TableCell>{row.customerName ?? "—"}</TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <span>
              {truncateCapabilityLabel(
                capabilityLabel(row.capabilityIdentifier, row.capabilityName),
              )}
            </span>
            {row.executionCount > 1 && <Badge variant="secondary">{row.executionCount}</Badge>}
          </div>
        </TableCell>
        <TableCell>{actionLabel(row.action)}</TableCell>
        <TableCell>{row.provider ?? "—"}</TableCell>
        <TableCell>
          <Badge variant={STATUS_VARIANTS[row.status]}>
            {t(`backoffice.capabilityExecutions.status.${row.status}`)}
          </Badge>
        </TableCell>
        <TableCell className="text-muted-foreground">
          <div title={reasonLabel(row)}>{truncateReason(reasonLabel(row))}</div>
          {row.blockedByCapabilityIdentifier && (
            <div className="text-xs">
              {t("backoffice.capabilityExecutions.table.blockedBy", {
                capability: capabilityLabel(row.blockedByCapabilityIdentifier),
              })}
            </div>
          )}
          {(row.blockedByCapabilityIdentifier || row.technicalExecutions.length > 1) && (
            <details className="mt-1 text-xs" onClick={(event) => event.stopPropagation()}>
              <summary className="cursor-pointer">
                {t("backoffice.capabilityExecutions.table.technicalExecutions")}
              </summary>
              {row.executionCount > 1 && (
                <p>{t("backoffice.capabilityExecutions.table.latestAttempt")}</p>
              )}
              <ol className="mt-1 space-y-1">
                {row.technicalExecutions.map((execution) => (
                  <li key={execution.id}>
                    {capabilityLabel(execution.capabilityIdentifier)}
                    {" · "}
                    {t(`backoffice.capabilityExecutions.status.${execution.status}`)}
                    {execution.reason &&
                      ` · ${translatedReasonLabel(execution.reason, execution.capabilityIdentifier)}`}
                  </li>
                ))}
              </ol>
            </details>
          )}
        </TableCell>
        <TableCell>
          <DateDisplay date={row.createdAt} />
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <BackofficeConversationExperimentsMultiSelect
            allLabel={t("backoffice.capabilityExecutions.filters.allCapabilities")}
            options={capabilityOptions}
            values={search.capabilityIdentifier === "all" ? undefined : selectedCapabilities}
            onChange={(values) =>
              updateSearch({
                capabilityIdentifier: values?.join(",") ?? "all",
                action: undefined,
                reason: undefined,
                page: 1,
              })
            }
          />
          <Select
            value={search.status ?? "all"}
            onValueChange={(value) => onChangeFilter("status", value)}
          >
            <SelectTrigger id="status" className="h-10 w-56 max-w-full shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CompanySelector
            value={search.companyId}
            onChange={(companyId) => updateSearch({ companyId, page: 1 })}
            className="h-10 min-w-44"
          />
          <FilterMenu fields={fields} labels={labels} />
        </div>

        <TimeRangeSelector
          value={search.timeFrame}
          onChange={({ timeFrame }) => updateSearch({ timeFrame, page: 1 })}
          presets={BACKOFFICE_RECENT_TIME_FRAME_OPTIONS}
        />
      </div>

      <Card className="flex flex-col min-w-xs">
        <div className="flex flex-col gap-4 p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <MetricCard
              title={t("backoffice.capabilityExecutions.kpis.success")}
              currentValue={String(data?.kpis.success ?? 0)}
              isPending={isPending}
              className="border-success"
            />
            <MetricCard
              title={t("backoffice.capabilityExecutions.kpis.unsuccess")}
              currentValue={String(data?.kpis.unsuccess ?? 0)}
              isPending={isPending}
              className="border-testing-1"
            />
            <MetricCard
              title={t("backoffice.capabilityExecutions.kpis.cached")}
              currentValue={String(data?.kpis.cached ?? 0)}
              isPending={isPending}
              className="border-muted-foreground"
            />
            <MetricCard
              title={t("backoffice.capabilityExecutions.kpis.skipped")}
              currentValue={String(data?.kpis.skipped ?? 0)}
              isPending={isPending}
              className="border-warning"
            />
            <MetricCard
              title={t("backoffice.capabilityExecutions.kpis.error")}
              currentValue={String(data?.kpis.error ?? 0)}
              isPending={isPending}
              className="border-destructive"
            />
          </div>
          <StackedActivityChart
            data={chartData}
            chartConfig={chartConfig}
            isPending={isPending}
            noDataT={{
              title: t("backoffice.capabilityExecutions.chart.empty"),
              description: t("backoffice.capabilityExecutions.chart.emptyDescription"),
            }}
            hideZeroTooltipEntries
          />
        </div>
      </Card>

      {!selectedCapabilityIdentifier && (
        <CapabilityDistributionTreemap
          data={data?.capabilityDistribution.map((item) => ({
            id: item.capabilityIdentifier,
            label: capabilityLabel(item.capabilityIdentifier),
            count: item.count,
          }))}
          isPending={isPending}
          title={t("backoffice.capabilityExecutions.treemap.capabilities.title")}
          description={t("backoffice.capabilityExecutions.treemap.capabilities.description")}
          emptyTitle={t("backoffice.capabilityExecutions.treemap.empty")}
          emptyDescription={t("backoffice.capabilityExecutions.treemap.emptyDescription")}
          itemLabel={t("backoffice.capabilityExecutions.treemap.tooltip.capability")}
          countLabel={t("backoffice.capabilityExecutions.treemap.tooltip.count")}
          percentageLabel={t("backoffice.capabilityExecutions.treemap.tooltip.percentage")}
          onItemClick={(capabilityIdentifier) =>
            updateSearch({
              capabilityIdentifier: capabilityIdentifier as CapabilityIdentifier,
              action: undefined,
              reason: undefined,
              page: 1,
            })
          }
        />
      )}
      {isUpdateOrder && !search.action && (
        <CapabilityDistributionTreemap
          data={data?.actionDistribution.map((item) => ({
            id: item.action,
            label: actionLabel(item.action),
            count: item.count,
          }))}
          isPending={isPending}
          title={t("backoffice.capabilityExecutions.treemap.actions.title")}
          description={t("backoffice.capabilityExecutions.treemap.actions.description")}
          emptyTitle={t("backoffice.capabilityExecutions.treemap.empty")}
          emptyDescription={t("backoffice.capabilityExecutions.treemap.emptyDescription")}
          itemLabel={t("backoffice.capabilityExecutions.treemap.tooltip.action")}
          countLabel={t("backoffice.capabilityExecutions.treemap.tooltip.count")}
          percentageLabel={t("backoffice.capabilityExecutions.treemap.tooltip.percentage")}
          onItemClick={(action) =>
            updateSearch({
              action: action as (typeof BackofficeCapabilityExecutionActionEnum)[number],
              reason: undefined,
              page: 1,
            })
          }
        />
      )}
      {isUpdateOrder && search.action && (
        <CapabilityDistributionTreemap
          data={data?.reasonDistribution.map((item) => ({
            id: item.reason,
            label: translatedReasonLabel(item.reason, CapabilityIdentifier.UpdateOrder),
            count: item.count,
          }))}
          isPending={isPending}
          title={t("backoffice.capabilityExecutions.treemap.reasons.title")}
          description={t("backoffice.capabilityExecutions.treemap.reasons.description", {
            action: actionLabel(search.action),
          })}
          emptyTitle={t("backoffice.capabilityExecutions.treemap.empty")}
          emptyDescription={t("backoffice.capabilityExecutions.treemap.emptyDescription")}
          itemLabel={t("backoffice.capabilityExecutions.treemap.tooltip.reason")}
          countLabel={t("backoffice.capabilityExecutions.treemap.tooltip.count")}
          percentageLabel={t("backoffice.capabilityExecutions.treemap.tooltip.percentage")}
          onItemClick={(reason) => updateSearch({ reason: reason as Search["reason"], page: 1 })}
        />
      )}
      {selectedCapabilityIdentifier && (
        <SelectedCapabilityMetrics
          metrics={data?.selectedCapabilityMetrics ?? null}
          isPending={isPending}
        />
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="pr-8">
              {t("backoffice.capabilityExecutions.table.company")}
            </TableHead>
            <TableHead>{t("backoffice.capabilityExecutions.table.customer")}</TableHead>
            <TableHead>{t("backoffice.capabilityExecutions.table.capability")}</TableHead>
            <TableHead>{t("backoffice.capabilityExecutions.table.action")}</TableHead>
            <TableHead>{t("backoffice.capabilityExecutions.table.provider")}</TableHead>
            <TableHead>{t("backoffice.capabilityExecutions.table.status")}</TableHead>
            <TableHead>{t("backoffice.capabilityExecutions.table.reason")}</TableHead>
            <TableHead>{t("backoffice.capabilityExecutions.table.date")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{renderRows()}</TableBody>
      </Table>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(page) => updateSearch({ page })}
        count={data?.rowCount}
      />
    </div>
  )
}
